if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        // Variant input can be null in some customized markup
        const vid = this.variantIdInput;
        if (vid) vid.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Size guard (web_dev 2026-10-26): a "Select a size/length" placeholder
        // variant is never sent to the cart; open the size prompt instead.
        if (this.isPlaceholderVariant(this.variantIdInput ? this.variantIdInput.value : '')) {
          if (typeof window.lvOpenSizePrompt === 'function') window.lvOpenSizePrompt();
          return;
        }

        // Re-acquire in case DOM was replaced by an app/theme script
        if (!this.submitButton) this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButtonText && this.submitButton)
          this.submitButtonText = this.submitButton.querySelector('span');

        // If there's no submit button, don't break the page
        if (!this.submitButton) return;

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinnerStart = this.querySelector('.loading__spinner');
        if (spinnerStart) spinnerStart.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        this.appendEngravingProperties(formData);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton
                ? this.submitButton.querySelector('.sold-out-message')
                : null;

              if (!soldOutMessage) return;

              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');

              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      if (this.cart) this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                if (this.cart) this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.submitButton) this.submitButton.classList.remove('loading');

            if (this.cart && this.cart.classList.contains('is-empty'))
              this.cart.classList.remove('is-empty');

            if (this.submitButton && !this.error) this.submitButton.removeAttribute('aria-disabled');

            const spinnerEnd = this.querySelector('.loading__spinner');
            if (spinnerEnd) spinnerEnd.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      appendEngravingProperties(formData) {
        const fontName = 'properties[Engraving Font]';
        const insideName = 'properties[Inside Engraving]';
        const outsideName = 'properties[Outside Engraving]';
        const engravingFieldNames = [fontName, insideName, outsideName];

        const getFieldValue = (name) => {
          const fieldInForm = this.form.querySelector(`[name="${name}"]`);
          if (fieldInForm) return (fieldInForm.value || '').trim();

          const sectionRoot = this.closest('.shopify-section');
          const selector = `#addEngravingModal [name="${name}"]`;
          const scopedField = sectionRoot ? sectionRoot.querySelector(selector) : null;
          if (scopedField) return (scopedField.value || '').trim();

          const globalField = document.querySelector(selector);
          return globalField ? (globalField.value || '').trim() : '';
        };

        const insideValue = getFieldValue(insideName);
        const outsideValue = getFieldValue(outsideName);
        const fontValue = getFieldValue(fontName);

        engravingFieldNames.forEach((name) => formData.delete(name));

        if (!insideValue && !outsideValue) return;

        if (insideValue) formData.append(insideName, insideValue);
        if (outsideValue) formData.append(outsideName, outsideValue);
        if (fontValue) formData.append(fontName, fontValue);
      }

      toggleSubmitButton(disable = true, text) {
        // Re-acquire if an app/theme replaced DOM after init
        if (!this.submitButton) this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButtonText && this.submitButton)
          this.submitButtonText = this.submitButton.querySelector('span');

        // If button still doesn't exist, don't crash variant selection
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      isPlaceholderVariant(variantId) {
        const list = (this.dataset.lvPlaceholderVariants || '').split(',');
        return !!variantId && list.indexOf(String(variantId)) !== -1;
      }

      get variantIdInput() {
        return this.form ? this.form.querySelector('[name=id]') : null;
      }
    }
  );
}
